<template>
  <router-link
      v-if="mode === 'link'"
      :to="route"
      class="relative table-row cursor-pointer align-middle after:absolute after:bottom-0 after:left-[24px] after:w-[calc(100%-48px)] after:border-b after:border-b-gpx_gray-800 last-of-type:after:hidden hover:bg-bg-2 sm:after:hidden"
  >
    <td
        v-for="(item, key, index) in dataCells"
        :key="index"
        class="h-[52px] py-[5px] pr-[20px] last-of-type:pr-[24px] 2xl:pr-[30px]"
        :class="[
        {
          'static left-[calc(100vw-56px)] top-0 w-[56px] select-none !pr-[12px] sm:relative sm:left-0':
            key === 'edit'
        },
        { 'min-w-[80px] max-w-0 truncate': truncate?.includes(key) },
        { 'pl-[24px] sm:pl-[32px]': +index === 0 }
      ]"
    >
      <div
          v-if="isRaw(key)"
          class="text-sm text-gpx_black-500 text-gpx_sm"
          :class="{
          'font-medium text-gpx_black-100': +index === 0
        }"
          :key="index + '-' + data.id"
      >
        <template v-if="typeof item === 'string' || typeof item === 'number'">
          {{ item }}
        </template>
        <span class="flex items-center space-x-2" v-else-if="item">
          <span>{{ item.label }}</span>
          <component :is="item.icon" class="h-4 w-4 shrink-0 text-yellow-600" />
        </span>
      </div>
      <div v-else-if="isHTML(key) && item" v-html="item"></div>

      <div
          v-if="key === 'status' || key === 'alert'"
          class="flex flex-wrap gap-x-[12px] gap-y-[6px]"
          :key="'hoverItem-' + data.id"
      >
        <Badge :status="item.status" :label="item.label" />
        <div
            v-if="item.link"
            class="inline-flex items-center text-xs text-gpx_xs font-medium text-gpx_porto-400"
        >
          {{ item.link.prefix }}&nbsp;
          <router-link
              :to="item.link.to"
              class="relative inline-flex items-center after:absolute after:bottom-0 after:left-0 after:w-full after:border-b after:border-b-gpx_porto-700"
          >
            <span>{{ item.link.label }}</span>
            <component v-if="item.link.icon" :is="item.link.icon" class="!h-4 !w-4" />
          </router-link>
        </div>
      </div>
      <div
          v-if="key === 'edit' && item"
          class="relative float-right flex h-[32px] w-[32px] cursor-pointer items-center justify-center sm:h-[48px] sm:w-[56px]"
          :class="{ 'after:opacity-100': editItemId === data.id }"
          :key="'edit-' + data.id"
          @click.stop.prevent="toggleEdit(data.id)"
          v-click-outside="closeEdit"
      >
        <Spinner v-if="data.isLoading" size="xs" />
        <EllipsisHorizontalIcon
            v-else
            class="heroicon-stroke-w-1.4 pointer-events-none h-6 w-6 text-gpx_gray-400"
            :class="{ '!text-gpx_porto-300': editItemId === data.id }"
        />
        <div
            class="fixed inset-x-0 bottom-0 z-50 min-w-[200px] rounded-[6px] border border-gpx_gray-900 bg-white px-[14px] pb-[18px] pt-[19px] drop-shadow-ui lg:absolute lg:bottom-auto lg:left-auto lg:top-full lg:translate-y-[8px]"
            v-if="editItemId === data.id && !data.isLoading"
        >
          <template v-for="item in editActions">
            <button
                :disabled="data.isLoading"
                @click.stop.prevent="($event) => doAction($event, item.action, data.id)"
                class="flex w-full cursor-pointer rounded-[4px] px-[10px] py-[6px] hover:bg-bg-2"
                :key="item.label"
                v-if="typeof item.shouldShow === 'function' ? item.shouldShow(data) : true"
            >
              <div class="mr-[12px]">
                <component
                    :is="item.icon"
                    class="heroicon-stroke-w-1.2 h-[20px] w-[20px] text-gpx_gray-200 group-hover:text-gpx_porto-400"
                />
              </div>
              <div class="text-sm font-medium text-gpx_black-300">
                {{ item.label }}
              </div>
            </button>
          </template>
        </div>
        <span
            v-if="editItemId === data.id && !data.isLoading"
            class="fixed inset-0 z-30 bg-gpx_black-100/20 lg:hidden"
        ></span>
      </div>
    </td>
  </router-link>
  <tr
      v-else
      class="relative cursor-pointer after:absolute after:bottom-0 after:left-[24px] after:w-[calc(100%-48px)] after:border-b after:border-b-gpx_gray-800 last-of-type:after:hidden hover:bg-bg-2 sm:after:hidden"
  >
    <td
        v-for="(item, key, index) in dataCells"
        :key="index"
        class="h-[52px] py-[5px] pr-[20px] last-of-type:pr-[24px] 2xl:pr-[30px]"
        :class="[
        {
          'static left-[calc(100vw-56px)] top-0 w-[56px] select-none !pr-[12px] sm:relative sm:left-0':
            key === 'edit'
        },
        { 'min-w-[80px] max-w-0 truncate': truncate?.includes(key) },
        { 'pl-[24px] sm:pl-[32px]': +index === 0 },
        columnWidth(key)
      ]"
    >
      <div
          v-if="isRaw(key)"
          class="inline text-sm text-gpx_sm text-gpx_black-500"
          :class="{
            'inline font-medium text-gpx_black-100': +index === 0
          }"
          :key="index + '-' + data.id"
      >
        <template v-if="typeof item === 'string' || typeof item === 'number'">
          {{ item }}
        </template>
        <template v-else-if="Array.isArray(item)">
          {{ item[0] }}
          <div class="text-gpx_xs text-gpx_gray-100">{{ item[1] }}</div>
        </template>
        <template v-else-if="item?.lines">
          <div v-for="(line, i) in item.lines" :key="i" class="whitespace-nowrap">{{ line }}</div>
        </template>
        <span class="flex items-center space-x-2" v-else-if="item">
          <span>{{ item.label }}</span>
          <component :is="item.icon" class="h-4 w-4 shrink-0 text-yellow-600" />
        </span>
      </div>

      <div
          v-if="key === 'status' || key === 'alert'"
          class="flex flex-wrap gap-x-[12px] gap-y-[6px]"
          :key="'hoverItem-' + data.id"
      >
        <Badge :status="item.status" :label="item.label" />
        <div
            v-if="item.link"
            class="inline-flex items-center text-xs text-gpx_xs font-medium text-gpx_porto-400"
        >
          {{ item.link.prefix }}&nbsp;
          <router-link
              :to="item.link.to"
              class="relative inline-flex after:absolute after:bottom-0 after:left-0 after:w-full after:border-b after:border-b-gpx_porto-700"
          >
            {{ item.link.label }}
          </router-link>
        </div>
      </div>
      <div
          v-if="key === 'edit' && item"
          class="relative float-right flex h-[32px] w-[32px] cursor-pointer items-center justify-center sm:h-[48px] sm:w-[56px]"
          :class="{ 'after:opacity-100': editItemId === data.id }"
          :key="'edit-' + data.id"
          @click.stop="toggleEdit(data.id)"
          v-click-outside="closeEdit"
      >
        <Spinner v-if="data.isLoading" size="xs" />
        <EllipsisHorizontalIcon
            v-else
            class="heroicon-stroke-w-1.4 pointer-events-none h-6 w-6 text-gpx_gray-400"
            :class="{ '!text-gpx_porto-300': editItemId === data.id }"
        />
        <div
            class="fixed inset-x-0 bottom-0 z-50 min-w-[200px] rounded-[6px] border border-gpx_gray-900 bg-white px-[14px] pb-[18px] pt-[19px] drop-shadow-ui lg:absolute lg:bottom-auto lg:left-auto lg:top-full lg:translate-y-[8px]"
            v-if="editItemId === data.id && !data.isLoading"
        >
          <template v-for="item in editActions">
            <button
                :disabled="data.isLoading"
                @click="($event) => doAction($event, item.action, data.id)"
                class="flex w-full cursor-pointer rounded-[4px] px-[10px] py-[6px] hover:bg-bg-2"
                :key="item.label"
                v-if="typeof item.shouldShow === 'function' ? item.shouldShow(data) : true"
            >
              <div class="mr-[12px]">
                <component
                    :is="item.icon"
                    class="heroicon-stroke-w-1.2 h-[20px] w-[20px] text-gpx_gray-200 group-hover:text-gpx_porto-400"
                />
              </div>
              <div class="whitespace-nowrap text-sm text-gpx_sm font-medium text-gpx_black-300">
                {{ item.label }}
              </div>
            </button>
          </template>
        </div>
        <span
            v-if="editItemId === data.id && !data.isLoading"
            class="fixed inset-0 z-30 bg-gpx_black-100/20 lg:hidden"
        ></span>
      </div>
      <div v-else-if="isHTML(key) && item" v-html="item"></div>
    </td>
  </tr>
</template>

<script>
import * as _ from 'lodash'
import vClickOutside from 'click-outside-vue3'
import {
  ArrowPathIcon,
  PauseIcon,
  PlayIcon,
  KeyIcon,
  EyeIcon,
  XMarkIcon,
  EllipsisHorizontalIcon
} from '@heroicons/vue/24/outline'
import Spinner from "./Spinner.vue";
import Badge from "./Badge.vue";

export default {
  name: 'DataTableItem',
  components: {
    Badge,
    Spinner,
    EllipsisHorizontalIcon,
    PauseIcon,
    PlayIcon,
    ArrowPathIcon,
    KeyIcon,
    EyeIcon,
    XMarkIcon
  },

  directives: {
    clickOutside: vClickOutside.directive
  },

  emits: ['setEditItemId'],
  props: {
    data: {
      type: Object,
      required: true
    },
    truncate: {
      type: Array,
      required: false
    },
    editActions: {
      type: Array,
      required: false
    },
    editItemId: {
      type: Number
    },
    mode: {
      type: String,
      validator(value) {
        return ['static', 'link', 'callback'].includes(value)
      },
      default: 'static'
    },
    route: {
      type: Object,
      required: false
    }
  },

  data() {
    return {
      hoverItem: null,
      isLoading: false
    }
  },

  computed: {
    dataCells() {
      return _.omit(this.data, ['id', 'isLoading'])
    },
    htmlCells() {
      return [
        'last_report',
        'last_login',
        'date',
        'content',
        'charge',
        'amount_remaining',
        'renewal',
        'nextRenewal',
      ]
    }
  },
  methods: {
    toggleEdit(id) {
      if (this.editItemId === id) {
        return this.$emit('setEditItemId', null)
      }
      this.$emit('setEditItemId', id)
    },
    closeEdit() {
      this.$emit('setEditItemId', null)
    },
    doAction($event, action, id) {
      if (action.mode === 'route') {
        this.$router.push(action.route.replace(':id', id))
      }
      if (action.mode === 'fn') {
        action.fn(id)
      }
      if (action.mode === 'async-fn') {
        this.actionInProgress = $event.currentTarget.id;
        action
            .fn(id)
            .catch(() => {
              const actionName =
                  typeof this.actionInProgress === 'string'
                      ? this.actionInProgress.toLowerCase()
                      : this.actionInProgress;
            })
            .finally(() => {
              this.actionInProgress = null;
            });
      }
    },
    isHTML(key) {
      return this.htmlCells.includes(key)
    },
    isRaw(key) {
      return !['status', 'alert', 'edit'].includes(key) && !this.isHTML(key)
    },
    columnWidth(item) {
      let styles
      switch (item) {
        case 'timestamp':
        case 'date':
          styles = 'w-[238px] whitespace-nowrap'
          break
        case 'edit':
          styles = 'w-[56px]'
          break
        default:
          break
      }
      return styles
    }
  }
}
</script>
